import { LitElement, html, nothing } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { AutoScrollController } from '../../controllers/autoscroll.controller.js';
import styles from './loquix-message-list.styles.js';

/**
 * @tag loquix-message-list
 * @summary Scrollable container for chat messages with auto-scroll support.
 *
 * @csspart list - Outer list wrapper.
 * @csspart scroll-container - The inner scrollable region.
 *
 * @slot - Default slot for loquix-message-item elements.
 * @slot empty-state - Content shown when no messages are present.
 * @slot loading - Loading indicator content.
 *
 * @cssprop [--loquix-message-gap] - Gap between messages.
 * @cssprop [--loquix-message-list-bg] - Background of the message list.
 * @cssprop [--loquix-message-list-padding] - Padding inside the scroll container.
 */
export class LoquixMessageList extends LitElement {
  static styles = [styles];

  /** Whether to auto-scroll to the bottom on new content. */
  @property({ type: Boolean, attribute: 'auto-scroll' })
  autoScroll = true;

  /** Enable virtual scrolling (no-op in Phase 1). */
  @property({ type: Boolean })
  virtualize = false;

  /** Whether to show timestamps on messages. */
  @property({ type: Boolean, attribute: 'show-timestamps' })
  showTimestamps = false;

  @query('.scroll-container')
  private _scrollContainer!: HTMLElement;

  @state()
  private _hasMessages = false;

  private _autoScrollController = new AutoScrollController(this);

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  protected firstUpdated(): void {
    if (this._scrollContainer && this.autoScroll) {
      this._autoScrollController.attach(this._scrollContainer);
    }
    this._checkForMessages();
  }

  // ---------------------------------------------------------------------------
  // Slot change detection
  // ---------------------------------------------------------------------------

  private _handleSlotChange(): void {
    this._checkForMessages();
  }

  private _checkForMessages(): void {
    const slot = this.shadowRoot?.querySelector<HTMLSlotElement>('slot:not([name])');
    if (slot) {
      const assigned = slot.assignedElements({ flatten: true });
      this._hasMessages = assigned.length > 0;
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  protected render() {
    return html`
      <div part="list" class="list">
        <div part="scroll-container" class="scroll-container">
          <slot name="loading"></slot>

          ${!this._hasMessages
            ? html`<slot name="empty-state"></slot>`
            : nothing}

          <slot @slotchange=${this._handleSlotChange}></slot>
        </div>
      </div>
    `;
  }
}
